import { Factory } from 'fishery';

import {
  Charge,
  ChargeFlat,
  ChargePercent,
  Fee,
  FeeStrategyType,
  PerIncidentFee,
  PerInstallmentFee,
  PerProductFee,
} from './types';

export type ChargeFlatTransient = Partial<
  Pick<ChargeFlat, 'amount' | 'includedFees'>
>;

export type ChargePercentTransient = Partial<
  Pick<ChargePercent, 'percent' | 'cap' | 'includedFees'>
>;

export type CommonFeeTransient = Partial<
  Pick<Fee, 'name' | 'displayName' | 'charge'>
> &
  Partial<Pick<PerIncidentFee, 'incidentType' | 'daysAfterToCharge'>> & {
    chargeType?: Charge['type'];
  };

export const chargeFlatFactory = Factory.define<
  ChargeFlat,
  ChargeFlatTransient
>(({ transientParams }) => ({
  type: 'FLAT',
  amount: transientParams.amount ?? 10,
  includedFees: transientParams.includedFees,
}));

export const chargePercentFactory = Factory.define<
  ChargePercent,
  ChargePercentTransient
>(({ transientParams }) => ({
  type: 'PERCENT',
  percent: transientParams.percent ?? 18,
  cap: transientParams.cap ?? 20,
  includedFees: transientParams.includedFees,
}));

export const perProductFeeFactory = Factory.define<
  PerProductFee,
  CommonFeeTransient
>(({ sequence, transientParams }) => {
  const charge: Charge =
    transientParams.charge ??
    (transientParams.chargeType === 'PERCENT'
      ? chargePercentFactory.build()
      : chargeFlatFactory.build());

  return {
    name: transientParams.name ?? `per_product_fee_${sequence}`,
    displayName: transientParams.displayName ?? 'Per Product Fee',
    subtype: 'PER_PRODUCT',
    charge,
    refundable: false,
    taxable: false,
  };
});

export const perInstallmentFeeFactory = Factory.define<
  PerInstallmentFee,
  CommonFeeTransient
>(({ sequence, transientParams }) => {
  const charge: Charge =
    transientParams.charge ??
    (transientParams.chargeType === 'FLAT'
      ? chargeFlatFactory.build()
      : chargePercentFactory.build({
          percent: 18,
          cap: 20,
          includedFees: ['policyFee', 'underwritingFee'],
        }));

  return {
    name: transientParams.name ?? `per_installment_fee_${sequence}`,
    displayName: transientParams.displayName ?? 'Payment Installment Fees',
    subtype: 'PER_INSTALLMENT',
    charge,
    refundable: false,
    taxable: false,
  };
});

export const perIncidentFeeFactory = Factory.define<
  PerIncidentFee,
  CommonFeeTransient
>(({ sequence, transientParams }) => {
  const charge: Charge =
    transientParams.charge ?? chargeFlatFactory.build({ amount: 10 });

  return {
    name: transientParams.name ?? `per_incident_fee_${sequence}`,
    displayName: transientParams.displayName ?? 'Per Incident Fee',
    subtype: 'PER_INCIDENT',
    charge,
    incidentType: transientParams.incidentType ?? 'REINSTATEMENT',
    daysAfterToCharge: transientParams.daysAfterToCharge,
    refundable: false,
    taxable: false,
  };
});

export type FeeStrategyTransient = Partial<
  Pick<FeeStrategyType, 'fees' | 'title'>
> & {
  kind?: FeeStrategyType['name'];
};

export const feeStrategyFactory = Factory.define<
  FeeStrategyType,
  FeeStrategyTransient
>(({ transientParams }) => {
  const kind = transientParams.kind ?? 'variable';
  return {
    name: kind,
    title:
      transientParams.title ??
      (kind === 'predictable' ? 'Predictable' : 'Variable'),
    fees: transientParams.fees ?? [],
  };
});
